import { useEffect, forwardRef, useImperativeHandle, useRef, useState, useContext } from "react";
import { fabric } from 'fabric';
import { AIEditContext } from "../../AIEditContext";

const formartNumber = (number, digits=2) => {
    return Number(number.toFixed(digits))
}

const MagicEraseCanvas = forwardRef((props, ref) => {
    useImperativeHandle(ref, () => ({
        clear,
        getDataURL,
        getSaveData,
        undo,
        loadSaveData,
    }));

    const {addWatermark} = useContext(AIEditContext)

    const bgCanvasRef = useRef()
    const bgInstanceRef = useRef()

    const canvasRef = useRef()
    const fabricInstanceRef = useRef()
    const [fabricInstance, setFabricInstance] = useState()

    const pencilBrushRef = useRef()
    const circleMouseRef = useRef()
    
    const moveableRef = useRef(props.moveable)
    const ctrlPressedRef = useRef(false)
    const displayScaleRef = useRef(props.displayScale)

    useEffect(() => {
        ctrlPressedRef.current = props.ctrlKeyPressed
    }, [props.ctrlKeyPressed])

    useEffect(() => {
        displayScaleRef.current = props.displayScale
    }, [props.displayScale])

    useEffect(() => {
        moveableRef.current = props.moveable
    }, [props.moveable])
    
    const getDataURL = () => {
        // console.log('getDataURL')
        const pathList = fabricInstanceRef.current.getObjects().filter(item => item.type == 'path')
        const tempCanvas = new fabric.StaticCanvas(null, {
            width: fabricInstanceRef.current.width,
            height: fabricInstanceRef.current.height,
            backgroundColor: '#000'
        })
        tempCanvas.add(...pathList)
        const exportPath = tempCanvas.toDataURL({ format: 'png', multiplier: 1 / displayScaleRef.current })
        return exportPath
    }
    const getSaveData = () => {
        // console.log('getSaveData')
        const bgSaveData = bgInstanceRef.current.toJSON()
        const saveData = fabricInstanceRef.current.toJSON()
        return {
            bgjson: bgSaveData,
            json: saveData,
            lines: saveData.objects.filter(item => item.type == 'path')
        }
    }
    const undo = () => {
        // console.log('undo')
        const pathList = fabricInstanceRef.current.getObjects().filter(item => item.type == 'path')
        if (pathList.length > 0) {
            fabricInstanceRef.current.remove(pathList.pop())
        }
        props.getIsDraw(pathList)
    }
    const loadSaveData = ({bgjson, json, lines}) => {
        // console.log('loadSaveData')
        bgInstanceRef.current.loadFromJSON(bgjson, () => {
            const bgImage = bgInstanceRef.current.get('backgroundImage')
            if (bgImage) bgImage.scale(props.displayScale)
            bgInstanceRef.current.setDimensions({ width: props.displayWidth, height: props.displayHeight})
            bgInstanceRef.current.renderAll()
        })
        fabricInstanceRef.current.loadFromJSON(json, () => {
            fabricInstanceRef.current.setDimensions({ width: props.displayWidth, height: props.displayHeight})
            const objs = fabricInstanceRef.current.getObjects()
            circleMouseRef.current = objs.find(item => item.type == 'circle')
            circleMouseRef.current.set({
                hasControls: false,
                hasBorders: false,
                selectable: false,
            })
            fabricInstanceRef.current.renderAll()
        })
        props.getIsDraw(lines)
    }
    const clear = () => {
        // console.log('clear')
        pencilBrushRef.current._reset() // 清除path
        const pathList = fabricInstanceRef.current.getObjects().filter(item => item.type == 'path')
        fabricInstanceRef.current.remove(...pathList)
    }

    useEffect(() => {
        // 马赛克
        const patternImageBase64 = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQCAYAAAAf8/9hAAAABHNCSVQICAgIfAhkiAAAADBJREFUOE9jfPP69X8GPEBYRASfNAPjqAHDIgz+AwG+iH7z5s1ZfPKMowYwDIMwAACuR1xZvSaHFwAAAABJRU5ErkJggg==';
        const pattern = new fabric.Pattern({
            source: patternImageBase64,
            repeat: 'repeat',
        })
        console.log(props.displayWidth, props.displayHeight)
        // 初始化canvas对象
        fabric.Image.fromURL(props.url, async (img) => {
            img.scale(props.displayScale) // 背景展示完全
            // 背景canvas
            const bgInstance = new fabric.Canvas(bgCanvasRef.current, {
                width: props.displayWidth,
                height: props.displayHeight,
                backgroundColor: pattern, // 加载马赛克背景
                backgroundImage: img, // 加载默认背景
                fireRightClick: false, // 启用右键
                stopContextMenu: true, // 禁用右键菜单
                isDrawingMode: false, // 禁用绘图模式
                // selection: false, // 禁用选中对象
            })
            await addWatermark(bgInstance) // 添加水印
            bgInstanceRef.current = bgInstance

            // 画笔canvas
            const instance = new fabric.Canvas(canvasRef.current, {
                width: props.displayWidth,
                height: props.displayHeight,
                fireRightClick: false, // 启用右键
                stopContextMenu: true, // 禁用右键菜单
                isDrawingMode: false, // 禁用绘图模式
                // selection: false, // 禁用选中对象
            })
            instance.includeDefaultValues = false            

            // 初始化画布
            const brush = new fabric.PencilBrush(instance)
            pencilBrushRef.current = brush
            brush.color = 'rgb(255, 255, 255)'
            brush.width = props.size * props.displayScale
            brush.limitedToCanvasSize = true
            instance.isDrawingMode = true
            instance.freeDrawingBrush = pencilBrushRef.current
            instance.freeDrawingCursor = 'pointer'

            // 画笔位置
            const circle = new fabric.Circle({
                left: 0,
                top: 0,
                radius: props.size * props.displayScale / 2,
                fill: 'rgb(255, 255, 255)',
                originX: 'center',
                originY: 'center',
                hasControls: false,
                hasBorders: false,
                selectable: false,
            })
            circleMouseRef.current = circle
            instance.bringToFront(circle)

            fabricInstanceRef.current = instance
            setFabricInstance(instance)
            props.setFabricInstance(bgInstance, instance)
        }, { crossOrigin: 'anonymous' })
    }, [])

    useEffect(() => {
        if (!bgInstanceRef.current) return
        const bgImage = bgInstanceRef.current.get('backgroundImage')
        bgImage.setSrc(props.url, () => bgInstanceRef.current.renderAll(), {crossOrigin: 'anonymous'})
    }, [props.url])

    useEffect(() => {
        if (!pencilBrushRef.current) return
        if (!fabricInstanceRef.current) return
        pencilBrushRef.current.width = props.size * props.displayScale
        circleMouseRef.current.set({ radius: props.size * props.displayScale / 2 })
        fabricInstanceRef.current.renderAll()
    }, [props.size, props.displayScale])

    useEffect(() => {
        if (!fabricInstanceRef.current) return
        if (props.moveable) {
            fabricInstanceRef.current.isDrawingMode = false
        } else {
            fabricInstanceRef.current.isDrawingMode = true
        }
    }, [props.moveable])

    const drawingRef = useRef()
    useEffect(() => {
        if (!fabricInstance) return

        fabricInstance.on('mouse:down', () => {
            if (moveableRef.current) return
            drawingRef.current = true
        })
        fabricInstance.on('mouse:move', (event) => {
            if (!fabricInstanceRef.current) return
            if (!circleMouseRef.current) return
            const { offsetX, offsetY } = event.e
            const canvasOffsetX = fabricInstanceRef.current.viewportTransform[4]
            const canvasOffsetY = fabricInstanceRef.current.viewportTransform[5]
            const zoom = fabricInstanceRef.current.getZoom()
            const x = (offsetX - canvasOffsetX) / zoom
            const y = (offsetY - canvasOffsetY) / zoom
            circleMouseRef.current.set({left: x, top: y})
            fabricInstanceRef.current.renderAll()
        })
        fabricInstance.on('mouse:up', () => {
            drawingRef.current = false
        })
        fabricInstance.on('path:created', ({path}) => {
            path.set({ selectable: false })
            const pathList = fabricInstanceRef.current.getObjects().filter(item => item.type == 'path')
            props.getIsDraw(pathList)
        })
        fabricInstance.on('mouse:wheel', function(event) {
            if (!circleMouseRef.current) return
            if (!ctrlPressedRef.current) return

            event.e.preventDefault();
            event.e.stopPropagation();

            const delta = event.e.deltaY;
            // 根据滚轮滚动方向调整缩放比例
            if (delta > 0) {
                props.changeBrushSize(-1)
            } else {
                props.changeBrushSize(1)
            }
        })
    }, [fabricInstance])

    useEffect(() => {
        if (!fabricInstanceRef.current) return
        
        const canvasScaleX = props.displayWidth / fabricInstanceRef.current.width
        const canvasScaleY = props.displayHeight / fabricInstanceRef.current.height
        
        const bgImage = bgInstanceRef.current.get('backgroundImage')
        if (bgImage) bgImage.scale(props.displayScale)

        bgInstanceRef.current.viewportTransform[4] = bgInstanceRef.current.viewportTransform[4] * canvasScaleX
        bgInstanceRef.current.viewportTransform[5] = bgInstanceRef.current.viewportTransform[5] * canvasScaleY
        bgInstanceRef.current.setDimensions({ width: props.displayWidth, height: props.displayHeight})
    
        const objs1 = bgInstanceRef.current.getObjects()
        objs1.forEach(item => {
            item.set({
                scaleX: item.scaleX * canvasScaleX,
                scaleY: item.scaleY * canvasScaleY
            })
            item.setCoords()               
        })

        fabricInstanceRef.current.viewportTransform[4] = fabricInstanceRef.current.viewportTransform[4] * canvasScaleX
        fabricInstanceRef.current.viewportTransform[5] = fabricInstanceRef.current.viewportTransform[5] * canvasScaleY
        fabricInstanceRef.current.setDimensions({ width: props.displayWidth, height: props.displayHeight})

        const objs = fabricInstanceRef.current.getObjects()
        objs.forEach(item => {
            if (item.type == 'path') {
                item.scale(item.scaleX * canvasScaleX)
                item.set({
                    left: formartNumber(item.left * canvasScaleX),
                    top: formartNumber(item.top * canvasScaleY),
                })
                item.setCoords() 
            } else {
                item.set({
                    width: formartNumber(item.width * canvasScaleX),
                    height: formartNumber(item.height * canvasScaleX),
                    left: formartNumber(item.left * canvasScaleX),
                    top: formartNumber(item.top * canvasScaleY),
                })
                item.setCoords()  
            }                  
        })

        bgInstanceRef.current.renderAll()
        fabricInstanceRef.current.renderAll()
    }, [props.displayWidth, props.displayHeight, props.displayScale])

    return (
        <div className="magicErase_canvasBox">
            <div className="magicErase_bgcanvas"><canvas ref={bgCanvasRef} /></div>
            <div className="magicErase_canvas"><canvas ref={canvasRef} /></div>
        </div>
    )
})

export default MagicEraseCanvas